import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Modal,
  Badge,
  Spinner,
} from "react-bootstrap";
import {
  PersonPlus,
  Pencil,
  Trash,
  Eye,
  Key,
  Send,
  ArrowLeft,
} from "react-bootstrap-icons";
import { Baseurl } from "../../baseurl";


const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNo: "",
    password: "",
    role: "employee",
    isActive: true,
    _id: id,
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");

  // Reset Password states
  const [resetPasswordData, setResetPasswordData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  // OTP states
  const [otpData, setOtpData] = useState({
    phoneNo: "",
    email: "",
  });

  // Fetch employee details on component mount
  useEffect(() => {
    fetchEmployeeDetails();
  }, [id]);

  const fetchEmployeeDetails = async () => {
    try {
      setFetching(true);
      const response = await axios.get(`${Baseurl}/api/employees/get/${id}`);
      console.log(response.data, "response.data");
      if (response.data.success) {
        const employee = response.data.emp;
        console.log(employee, "employee");
        setFormData({
          name: employee.name || "",
          email: employee.email || "",
          phoneNo: employee.phoneNo || "",
          password: "", // Don't prefill password for security
          role: employee.role || "employee",
          isActive: employee.isActive !== false, // Default to true if undefined
          _id: employee._id || id,
        });
      } else {
        showAlert(
          response.data.message || "Failed to fetch employee details",
          "danger",
        );
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error fetching employee details",
        "danger",
      );
      console.error("Error fetching employee:", error);
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create a clean update object - remove empty password if not changed
      const updateData = { ...formData, _id: id };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await axios.put(
        `${Baseurl}/api/employees/edit/${id}`,
        updateData,
      );

      if (response.data.success) {
        showAlert(
          response.data.message || "Employee updated successfully!",
          "success",
        );
        // Optionally refresh data
        fetchEmployeeDetails();
      } else {
        showAlert(
          response.data.message || "Failed to update employee",
          "danger",
        );
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error updating employee",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant) => {
    setAlert({ show: true, message, variant });
    setTimeout(() => {
      setAlert({ show: false, message: "", variant: "success" });
    }, 5000);
  };

  // Open modal for actions
  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);

    if (type === "resetPassword") {
      setResetPasswordData((prev) => ({ ...prev, email: formData.email }));
    }
    if (type === "sendOtp") {
      setOtpData((prev) => ({
        ...prev,
        phoneNo: formData.phoneNo,
        email: formData.email,
      }));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setResetPasswordData({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setOtpData({
      phoneNo: "",
      email: "",
    });
  };

  // Handle delete employee
  const handleDelete = async () => {
    try {
      const response = await axios.delete(`${Baseurl}/api/employees/delete`, {
        data: { _id: id },
      });

      if (response.data.success) {
        showAlert(
          response.data.message || "Employee deleted successfully!",
          "success",
        );
        // Navigate back to employee list after deletion
        setTimeout(() => {
          navigate("/employees"); // Adjust this route as needed
        }, 1500);
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error deleting employee",
        "danger",
      );
    }
  };

  // Send OTP
  const handleSendOtp = async () => {
    try {
      const response = await axios.post(`${Baseurl}/api/employees/send-otp`, {
        phoneNo: otpData.phoneNo,
      });

      if (response.data.success) {
        showAlert(response.data.message || "OTP sent successfully!", "success");
      } else {
        showAlert(response.data.message || "Failed to send OTP", "danger");
      }
      closeModal();
    } catch (error) {
      showAlert(error.response?.data?.message || "Error sending OTP", "danger");
    }
  };

  // Send reset password OTP
  const handleSendResetOtp = async () => {
    try {
      const response = await axios.post(
        `${Baseurl}/api/employees/send-reset-otp`,
        {
          email: resetPasswordData.email,
        },
      );

      if (response.data.success) {
        showAlert(
          response.data.message || "Reset OTP sent to email!",
          "success",
        );
      } else {
        showAlert(
          response.data.message || "Failed to send reset OTP",
          "danger",
        );
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error sending reset OTP",
        "danger",
      );
    }
  };

  // Reset password with OTP
  const handleResetPassword = async () => {
    if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      showAlert("Passwords do not match!", "danger");
      return;
    }

    try {
      const response = await axios.post(
        `${Baseurl}/api/employees/change-password-otp`,
        {
          email: resetPasswordData.email,
          otp: resetPasswordData.otp,
          newPassword: resetPasswordData.newPassword,
        },
      );

      if (response.data.success) {
        showAlert(
          response.data.message || "Password reset successfully!",
          "success",
        );
        closeModal();
      } else {
        showAlert(
          response.data.message || "Failed to reset password",
          "danger",
        );
      }
    } catch (error) {
      showAlert(
        error.response?.data?.message || "Error resetting password",
        "danger",
      );
    }
  };

  if (fetching) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading employee details...</p>
        </div>
      </Container>
    );
  }
  console.log(formData, "formData");
  return (
    <Container fluid className="py-4">
      {/* Alert */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          onClose={() =>
            setAlert({ show: false, message: "", variant: "success" })
          }
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <Row>
        <Col>
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Button
                  variant="light"
                  size="sm"
                  className="me-3"
                  onClick={() => navigate(-1)} // Go back to previous page
                >
                  <ArrowLeft className="me-1" /> Back
                </Button>
                <h5 className="mb-0">
                  <Pencil className="me-2" />
                  Edit Employee: {formData.name}
                </h5>
              </div>
              <Badge bg="info">ID: {id}</Badge>
            </Card.Header>

            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter full name"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter email address"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number *</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phoneNo"
                        value={formData.phoneNo}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role *</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="agent">Agent</option>
                        <option value="inspector">Inspector</option>
                        <option value="yardOwner">YardOwner</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        id="isActive"
                        label="Active Employee"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="mt-3"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="outline-info"
                    onClick={() => openModal("view")}
                  >
                    <Eye className="me-1" />
                    View Details
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => openModal("delete")}
                  >
                    <Trash className="me-1" />
                    Delete
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => fetchEmployeeDetails()}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                  <Button variant="warning" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Pencil className="me-1" />
                        Update Employee
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      {/* View Employee Modal */}
      <Modal
        show={showModal && modalType === "view"}
        onHide={closeModal}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Eye className="me-2" />
            Employee Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formData && (
            <Row>
              <Col md={6}>
                <h6>Personal Information</h6>
                <p>
                  <strong>Name:</strong> {formData.name}
                </p>
                <p>
                  <strong>Email:</strong> {formData.email}
                </p>
                <p>
                  <strong>Phone:</strong> {formData.phoneNo}
                </p>
                <p>
                  <strong>Role:</strong>{" "}
                  <Badge bg="info">{formData.role}</Badge>
                </p>
              </Col>
              <Col md={6}>
                <h6>Account Status</h6>
                <p>
                  <strong>Status:</strong>{" "}
                  {formData.isActive ? (
                    <Badge bg="success">Active</Badge>
                  ) : (
                    <Badge bg="secondary">Inactive</Badge>
                  )}
                </p>
                <p>
                  <strong>Employee ID:</strong> {id}
                </p>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showModal && modalType === "delete"} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Trash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete employee{" "}
          <strong>{formData.name}</strong>?
          <br />
          <small className="text-danger">This action cannot be undone.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Employee
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EditEmployee;
